import { AmbientGlow, ScrollReveal } from '@/components/design-system';
import { motion } from 'framer-motion';
import { Building2, Recycle, Truck } from 'lucide-react';

// Node layout in a 0-400 x 0-260 viewBox: households (left) -> agents (center) -> companies (right)
const households = [
  { x: 40, y: 50 },
  { x: 40, y: 130 },
  { x: 40, y: 210 },
];
const agents = [
  { x: 200, y: 90 },
  { x: 200, y: 170 },
];
const companies = [
  { x: 360, y: 50 },
  { x: 360, y: 130 },
  { x: 360, y: 210 },
];

// Each connection: from household index -> agent index, and agent index -> company index
const householdToAgent = [
  [0, 0],
  [1, 0],
  [1, 1],
  [2, 1],
];
const agentToCompany = [
  [0, 0],
  [0, 1],
  [1, 1],
  [1, 2],
];

function curvePath(x1: number, y1: number, x2: number, y2: number) {
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
}

export function NetworkDiagram() {
  return (
    <section className='relative overflow-hidden py-24 md:py-32'>
      <AmbientGlow color='accent' size={480} position='top-0 left-1/2 -translate-x-1/2 -translate-y-1/2' />

      <div className='container relative'>
        <ScrollReveal className='mx-auto max-w-lg text-center'>
          <span className='text-overline'>The Network</span>
          <h2 className='font-heading mt-2 text-headline text-foreground'>
            One Recovery Loop, Everyone Connected
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className='mx-auto mt-14 max-w-4xl'>
          <svg viewBox='0 0 400 260' className='w-full' aria-hidden='true'>
            <defs>
              <linearGradient id='linkGradient' x1='0%' y1='0%' x2='100%' y2='0%'>
                <stop offset='0%' stopColor='hsl(var(--primary))' />
                <stop offset='100%' stopColor='hsl(var(--accent))' />
              </linearGradient>
            </defs>

            {/* Connecting lines: draw themselves via animated pathLength */}
            {householdToAgent.map(([hIdx, aIdx], i) => {
              const h = households[hIdx];
              const a = agents[aIdx];
              return (
                <motion.path
                  key={`ha-${i}`}
                  d={curvePath(h.x, h.y, a.x, a.y)}
                  stroke='url(#linkGradient)'
                  strokeWidth={1.5}
                  fill='none'
                  strokeLinecap='round'
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.7 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.12, ease: 'easeInOut' }}
                />
              );
            })}
            {agentToCompany.map(([aIdx, cIdx], i) => {
              const a = agents[aIdx];
              const c = companies[cIdx];
              return (
                <motion.path
                  key={`ac-${i}`}
                  d={curvePath(a.x, a.y, c.x, c.y)}
                  stroke='url(#linkGradient)'
                  strokeWidth={1.5}
                  fill='none'
                  strokeLinecap='round'
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.7 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.7 + i * 0.12, ease: 'easeInOut' }}
                />
              );
            })}

            {/* Household nodes */}
            {households.map((n, i) => (
              <motion.g
                key={`h-${i}`}
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <circle cx={n.x} cy={n.y} r={16} fill='hsl(var(--card))' stroke='hsl(var(--primary))' strokeWidth={1.5} />
                <Recycle x={n.x - 7} y={n.y - 7} width={14} height={14} color='hsl(var(--primary))' strokeWidth={2} />
              </motion.g>
            ))}

            {/* Agent nodes */}
            {agents.map((n, i) => (
              <motion.g
                key={`a-${i}`}
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
              >
                <circle cx={n.x} cy={n.y} r={18} fill='hsl(var(--card))' stroke='hsl(var(--accent))' strokeWidth={1.5} />
                <Truck x={n.x - 8} y={n.y - 8} width={16} height={16} color='hsl(var(--accent))' strokeWidth={2} />
              </motion.g>
            ))}

            {/* Company nodes */}
            {companies.map((n, i) => (
              <motion.g
                key={`c-${i}`}
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.8 + i * 0.1 }}
              >
                <circle cx={n.x} cy={n.y} r={16} fill='hsl(var(--card))' stroke='hsl(var(--primary))' strokeWidth={1.5} />
                <Building2 x={n.x - 7} y={n.y - 7} width={14} height={14} color='hsl(var(--primary))' strokeWidth={2} />
              </motion.g>
            ))}
          </svg>

          <div className='mt-2 flex justify-between px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            <span>Households</span>
            <span>Agents</span>
            <span>Companies</span>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

export default NetworkDiagram;
